import { html, nothing, type TemplateResult } from 'lit';
import { overviewCard, statusBadge, statusColor } from '@vitesse/base-ui/components/templates';
import { modelStatusLabel } from './model-status';
import { machineCountOf } from './machine-graph';
import { t } from '@vitesse/base-ui/i18n';
import type { ModelView } from '../../../core/domain/model';

export interface ModelCardHandlers {
  /** Navigate to the model detail page. */
  onClick: () => void;
  /** Hover dwell → warm the SCXML source so the detail opens instantly. */
  onHover?: () => void;
  /** Friendly gateway label — set in the flat home views (shows a leading
   *  "which gateway" badge), omitted on the gateway detail page (redundant). */
  gatewayName?: string;
}

/*
 * modelOverviewCard — the ONE reusable model list-card (home + gateway Models
 * tab share it). Same base-overview-card shape as every other entity card:
 * entity icon + name, description (placeholder when none), a status-badges row,
 * clickable → detail, hover → SCXML prefetch.
 *
 * The runtime-status badge is live. A group (a model with child machines) also carries a blue
 * "Linked group · N machines" badge after it, the words of the Machines tile's diagram, so a group
 * reads apart from a single machine in the list. The controllers-linked + recording chips are
 * PLACEHOLDERS (gray) — the agent shows them and we mirror the shape so every
 * card carries the same structure; they go live when ModelView carries
 * controller-links / recording state (fleet + historian). The download /
 * duplicate / delete actions wait on their RPCs.
 */
export function modelOverviewCard(m: ModelView, h: ModelCardHandlers): TemplateResult {
  const group =
    (m.children ?? []).length > 0
      ? statusBadge('blue', `${t('model.machinesGroup')} · ${t('model.machinesCount', { count: machineCountOf(m) })}`, 'workflow')
      : nothing;
  return overviewCard({
    title: m.name,
    entityType: 'model',
    description: m.description || t('model.noDescription'),
    clickable: true,
    onClick: h.onClick,
    onHover: h.onHover,
    badges: html`${h.gatewayName ? statusBadge('gateway', h.gatewayName, 'hard-drive') : nothing}${statusBadge(
      statusColor(m.status),
      modelStatusLabel(m.status),
    )}${group}${statusBadge('gray', t('model.noControllersLinked'))}${statusBadge('gray', t('model.notRecording'))}`,
  });
}
